
import { computed, defineComponent, onMounted } from "vue";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { storeToRefs } from "pinia";
import { useVaultStore } from "@/stores/vault";
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default defineComponent({
  name: "vault-addresses",
  setup() {
    const vault = useVaultStore();
    const {
      addresses,
      isAddressesLoading,
      summaryBtcPrice,
      addressesUpdatedAt,
      addressesPage,
      addressesPageSize,
      addressesHasMore,
      addressesTotalCount,
    } = storeToRefs(vault);

    const formatDateTimezone = (dateStamp: number | Date) =>
      dayjs(dateStamp).format("YYYY/MM/DD HH:mm:ss z");

    const formattedAddressesUpdated = computed(() => {
      const updated = addressesUpdatedAt?.value;
      if (!updated) return "";
      return formatDateTimezone(updated);
    });

    const totalPages = computed(() => {
      const total = addressesTotalCount.value || 0;
      const size = addressesPageSize.value || 1;
      return Math.max(1, Math.ceil(total / size));
    });

    const maxPage = computed(() => totalPages.value - 1);

    const windowStart = computed(() => Math.max(0, addressesPage.value - 2));
    const windowEnd = computed(() =>
      Math.min(maxPage.value, addressesPage.value + 2)
    );

    const pageWindow = computed(() => {
      const pages: number[] = [];
      for (let p = windowStart.value; p <= windowEnd.value; p += 1) {
        pages.push(p);
      }
      return pages;
    });

    const showLeftEllipsis = computed(() => windowStart.value > 0);
    const showRightEllipsis = computed(() => windowEnd.value < maxPage.value);

    const fetchAddresses = () => vault.fetchAddresses();

    const goToPage = (page: number) => {
      if (isAddressesLoading.value) return;
      const max = maxPage.value;
      const target = Math.min(Math.max(0, page), max);
      vault.fetchAddresses({ page: target });
    };

    const nextAddressesPage = () => {
      goToPage(addressesPage.value + 1);
    };

    const prevAddressesPage = () => {
      goToPage(addressesPage.value - 1);
    };

    const addressTypeLabel = (type: string) => {
      switch (type) {
        case "change":
          return "Cambio";
        case "deposit":
          return "Depósito";
        default:
          return "Desconocida";
      }
    };

    const addressTypeClass = (type: string) => {
      switch (type) {
        case "change":
          return "badge bg-warning";
        case "deposit":
          return "badge bg-success";
        default:
          return "badge bg-dark";
      }
    };

    const formatUSD = (amountBtc: string | number) => {
      const btc =
        typeof amountBtc === "number" ? amountBtc : parseFloat(amountBtc);
      const usdValue = btc * (summaryBtcPrice.value || 0);
      return usdValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    onMounted(() => {
      fetchAddresses();
    });

    return {
      addresses,
      isAddressesLoading,
      summaryBtcPrice,
      addressesUpdatedAt,
      addressesPage,
      addressesPageSize,
      addressesTotalCount,
      addressesHasMore,
      totalPages,
      pageWindow,
      showLeftEllipsis,
      showRightEllipsis,
      formattedAddressesUpdated,
      fetchAddresses,
      goToPage,
      nextAddressesPage,
      prevAddressesPage,
      addressTypeLabel,
      addressTypeClass,
      formatDateTimezone,
      formatUSD,
    };
  },
});
